import { Editor, TLShape } from '@tldraw/tldraw'
import Color from 'colorjs.io'
import { getBackdropColor, getBackdropShape, getShapeColor } from '../../helpers'
import { shuffle, sortBy } from 'lodash'
import { ImageShapeUtil } from 'components/VisualStudioPlayground/Shapes/ImageShape/ImageShapeUtil'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { IFillStyle } from '../../StyleProps'
import { FrameShape } from 'components/VisualStudioPlayground/Shapes/FrameShape/Types'
import { GeoShape } from 'components/VisualStudioPlayground/Shapes/GeoShape/Types'

const TARGET_CONTRAST = 30
const TARGET_TEXTBOX_CONTRAST = 50

/**
 * Picks a new color for a shape based on the backdrop color, the current
 * color, and the color palette. The strategy is to pick a color that has
 * a high contrast with the backdrop color. Text boxes have a higher target
 * contrast than other shapes to ensure text is legible.
 */
export const pickNewColor = (editor: Editor, shape: TLShape, colors: Color[]): Color => {
  const currentColor = getShapeColor(editor, shape)
  const backdropShape = getBackdropShape(editor, shape)
  let newColor: Color | undefined

  if (!backdropShape) {
    newColor = getNextColorInPalette(colors, currentColor)
  } else {
    const backdropColor = getBackdropColor(editor, shape)
    newColor = getHighContrastColor(
      colors,
      backdropColor,
      shape.type === 'textBox' ? TARGET_TEXTBOX_CONTRAST : TARGET_CONTRAST,
    )
  }

  return newColor || currentColor
}

/**
 * Returns the next color in the palette after the current color.
 */
const getNextColorInPalette = (colors: Color[], currentColor: Color): Color | undefined => {
  const currentColorIndex = colors.findIndex((color) => color.equals(currentColor))
  return [...colors.slice(currentColorIndex + 1), ...colors.slice(0, currentColorIndex + 1)].find(
    (color) => !color.equals(currentColor),
  )
}

/**
 * Returns a color from the palette that has a high contrast with the backdrop color.
 * If no color has a high enough contrast, the color with the highest contrast is returned.
 * If the highest contrast color does not have a high enough contrast, its lightness is adjusted
 * to achieve the target contrast.
 */
const getHighContrastColor = (
  colors: Color[],
  backdropColor: Color,
  targetContrast = TARGET_CONTRAST,
): Color | undefined => {
  const colorsSortedByContrast = sortBy(colors, (a) => -a.contrast(backdropColor, 'Lstar'))
  const highContrastColors = colorsSortedByContrast.filter(
    (color) => color.contrast(backdropColor, 'Lstar') > targetContrast,
  )

  let newColor = shuffle(highContrastColors).find((color) => !color.equals(backdropColor))

  if (newColor) {
    return newColor
  }

  if (backdropColor.clone().to('lab').l > 65) {
    return new Color('black')
  }

  return new Color('white')
}

/**
 * Updates the shape's color based on its type. Image shapes with a colorset are colorised
 * by interpolating the given color between itself and either white or black, depending on
 * the color's lightness. This is done to ensure a wider range of colors in the
 * colorset.
 */
export const coloriseShape = (editor: Editor, shape: TLShape, color: Color) => {
  if (shape.type === 'frame' || shape.type === 'geometric') {
    const currentFill = (shape as FrameShape | GeoShape).props.fill as IFillStyle
    const opacity =
      currentFill.type === 'linearGradient'
        ? Math.min(...currentFill.stops.map((stop) => stop.opacity))
        : currentFill.opacity
    editor.updateShape({
      ...shape,
      props: { fill: { type: 'solidColor', color: color.toString({ format: 'hex' }), opacity } },
    })
  }

  if (shape.type === 'textBox') {
    editor.updateShape({ ...shape, props: { color: color.toString({ format: 'hex' }) } })
  }

  if (shape.meta.colorSet) {
    const util = editor.getShapeUtil(shape) as ImageShapeUtil
    const colorRange = color.range(color.to('lab').coords[0] < 50 ? '#fff' : '#000')
    const colorsCount = Object.keys(shape.meta.colorSet).length

    if (colorsCount === 1) {
      util.colorise(
        shape as ImageShape,
        Object.fromEntries([[Object.keys(shape.meta.colorSet)[0], color.toString({ format: 'hex' })]]),
      )
      return
    }

    const newColorSet = Object.fromEntries(
      Object.entries(shape.meta.colorSet).map((entry, i) => {
        const n = (1 / colorsCount) * (i + 0.5)
        return [entry[0], colorRange(n).to('srgb').toString({ format: 'hex' })]
      }),
    )
    util.colorise(shape as ImageShape, newColorSet)
  }
}
